import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, statusColor } from "@/lib/api";
import { resolveImageUrl } from "@/components/ImageInput";
import { useAuth } from "@/context/AuthContext";
import { Phone, Trophy, CalendarBlank, MapPin, PlayCircle, ArrowRight, ArrowLeft, SignOut } from "@phosphor-icons/react";

const CONTACT_PHONE = "+91-9970228557";
const CONTACT_PHONE_TEL = "+919970228557";

const TABS = [
  { key: "live", label: "Today's Auction" },
  { key: "upcoming", label: "Upcoming Auction" },
  { key: "past", label: "Past Auction" },
];

function AuctionRowCard({ a }) {
  const start = a.start_date ? new Date(a.start_date) : null;
  const bannerSrc = a.banner_url ? resolveImageUrl(a.banner_url) : null;
  return (
    <Link
      to={`/auction/${a.id}`}
      className="flex items-center gap-4 bg-white shadow-md hover:shadow-xl transition-shadow rounded-lg p-3 border border-gray-100 hover:border-blue-200 min-w-[280px] max-w-[420px]"
      data-testid={`auction-card-${a.id}`}
    >
      <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-100 flex-shrink-0 grid place-items-center bg-gradient-to-br from-blue-50 to-blue-100">
        {bannerSrc ? (
          <img src={bannerSrc} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Trophy weight="fill" size={30} className="text-blue-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-gray-900 uppercase tracking-wide truncate" data-testid={`auction-name-${a.id}`}>{a.name}</div>
        {start && (
          <div className="text-xs text-blue-600 flex items-center gap-1 mt-0.5">
            <CalendarBlank size={12} /> {start.toLocaleDateString("en-GB")}
          </div>
        )}
        {a.venue && (
          <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
            <MapPin size={12} /> {a.venue}
          </div>
        )}
      </div>
      {a.status === "live" && <span className="text-[10px] font-bold uppercase bg-red-500 text-white px-2 py-1 rounded">LIVE</span>}
    </Link>
  );
}

function AuctionsCarousel({ tabKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(0);
  const perPage = 2;

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/public/auctions?status=${tabKey}`);
        if (!cancel) setItems(data);
      } catch (e) {
        if (!cancel) setItems([]);
      } finally { if (!cancel) setLoading(false); }
      setStart(0);
    })();
    return () => { cancel = true; };
  }, [tabKey]);

  const canPrev = start > 0;
  const canNext = start + perPage < items.length;
  const slice = items.slice(start, start + perPage);

  return (
    <div className="relative">
      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading auctions…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-400" data-testid="auction-empty">No auctions in this category.</div>
      ) : (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setStart(Math.max(0, start - perPage))}
            disabled={!canPrev}
            className={`w-9 h-9 rounded-full border grid place-items-center transition-colors flex-shrink-0 ${canPrev ? "border-gray-300 text-gray-700 hover:bg-gray-100" : "border-gray-100 text-gray-300 cursor-not-allowed"}`}
            data-testid="carousel-prev"
            aria-label="Previous"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 flex flex-wrap gap-4 justify-center min-h-[100px]">
            {slice.map((a) => <AuctionRowCard key={a.id} a={a} />)}
          </div>
          <button
            type="button"
            onClick={() => setStart(Math.min(items.length - perPage, start + perPage))}
            disabled={!canNext}
            className={`w-9 h-9 rounded-full border grid place-items-center transition-colors flex-shrink-0 ${canNext ? "border-gray-300 text-gray-700 hover:bg-gray-100" : "border-gray-100 text-gray-300 cursor-not-allowed"}`}
            data-testid="carousel-next"
            aria-label="Next"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      )}
      <div className="flex justify-end mt-4">
        <Link to="/auctions" className="text-blue-600 font-semibold text-sm hover:underline" data-testid="view-all-link">View All →</Link>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [tab, setTab] = useState("live");
  const { user, logout } = useAuth();

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Inter, sans-serif" }} data-testid="home-page">
      {/* Top red contact bar */}
      <div className="bg-red-600 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 flex items-center justify-between">
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-2 font-semibold" data-testid="home-phone-link">
            <Phone size={14} weight="fill" /> {CONTACT_PHONE}
          </a>
          {user ? (
            <span className="hidden sm:inline uppercase tracking-widest text-xs">Signed in as {user.name || user.email}</span>
          ) : (
            <a href="#pricing" className="hidden sm:inline uppercase tracking-widest text-xs">Pricing & Plans</a>
          )}
        </div>
      </div>

      {/* White navigation */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-brand">
            <img src="/nitisoft-logo.png" alt="NitiSoft Auction" className="h-10" />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">NitiSoft</span>
              <span className="text-[10px] text-gray-500 uppercase tracking-widest">Player Auction</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-semibold text-gray-800">
            <button onClick={() => setTab("live")} className={`hover:text-blue-600 ${tab === "live" ? "text-blue-600" : ""}`}>Today Auctions</button>
            <button onClick={() => setTab("upcoming")} className={`hover:text-blue-600 ${tab === "upcoming" ? "text-blue-600" : ""}`}>Upcoming Auctions</button>
            <button onClick={() => setTab("past")} className={`hover:text-blue-600 ${tab === "past" ? "text-blue-600" : ""}`}>Past Auctions</button>
            <Link to="/pricing" className="hover:text-blue-600" data-testid="nav-pricing-link">Pricing</Link>
            <a href="#about" className="hover:text-blue-600">About</a>
          </div>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to={user.role === "superadmin" ? "/superadmin" : user.role === "team_owner" ? "/team" : "/dashboard"}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold px-4 py-2 rounded transition-colors"
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={logout}
                className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded transition-colors flex items-center gap-1"
                data-testid="nav-logout-btn"
              >
                <SignOut size={14} /> Logout
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link
                to="/register"
                className="border-2 border-red-600 text-red-600 hover:bg-red-50 text-sm font-bold px-4 py-2 rounded transition-colors"
                data-testid="nav-register-btn"
              >
                Register
              </Link>
              <Link
                to="/login"
                className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold px-4 py-2 rounded transition-colors"
                data-testid="nav-login-btn"
              >
                Login
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Blue hero */}
      <section className="relative bg-blue-500 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle at 20% 30%, white 0%, transparent 30%), radial-gradient(circle at 80% 60%, white 0%, transparent 20%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 sm:py-24 grid md:grid-cols-2 gap-8 items-center relative">
          <div>
            <h1 className="font-black uppercase leading-[1.05] tracking-tight" style={{ fontSize: "clamp(48px, 6vw, 84px)" }}>
              NitiSoft<br />Player Auction
            </h1>
            <p className="text-2xl sm:text-3xl mt-4 opacity-95 font-medium">Live Player Auction Application</p>
            <p className="mt-4 text-white/90 max-w-lg">
              End-to-end auction platform for cricket tournaments — live bidding, LED & stream overlays, team management, and reporting. Deploy in minutes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/register" className="bg-white text-blue-600 font-bold uppercase text-sm px-6 py-3 rounded shadow-md hover:shadow-lg transition-shadow" data-testid="hero-register">
                Organize an Auction
              </Link>
              <a href="#auctions" className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-sm px-6 py-3 rounded shadow-md transition-colors flex items-center gap-2">
                <PlayCircle weight="fill" size={18} /> Watch Live
              </a>
            </div>
          </div>
          <div className="hidden md:flex justify-center">
            {/* Illustrated hero graphic (pure SVG) */}
            <svg viewBox="0 0 500 400" className="w-full max-w-md drop-shadow-2xl" aria-hidden="true">
              <defs>
                <linearGradient id="stage" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#22d3ee" />
                  <stop offset="1" stopColor="#0ea5e9" />
                </linearGradient>
              </defs>
              {/* Stage */}
              <ellipse cx="250" cy="360" rx="200" ry="20" fill="rgba(0,0,0,0.15)" />
              <ellipse cx="250" cy="340" rx="180" ry="30" fill="url(#stage)" />
              {/* Auctioneer podium */}
              <rect x="210" y="230" width="80" height="80" fill="#a16207" />
              <rect x="220" y="240" width="60" height="60" fill="#ca8a04" />
              {/* Auctioneer body */}
              <circle cx="250" cy="180" r="28" fill="#fbbf24" />
              <rect x="230" y="200" width="40" height="60" rx="8" fill="#dc2626" />
              {/* Gavel */}
              <line x1="290" y1="180" x2="330" y2="150" stroke="#78350f" strokeWidth="6" strokeLinecap="round" />
              <rect x="315" y="130" width="40" height="24" rx="6" fill="#a16207" />
              {/* Bid signs */}
              {[130, 380, 100, 400, 150].map((x, i) => (
                <g key={i} transform={`translate(${x}, ${240 + i * 15}) rotate(${-15 + i * 6})`}>
                  <rect width="34" height="24" rx="3" fill="white" stroke="#0f172a" strokeWidth="1.5" />
                  <text x="17" y="16" textAnchor="middle" fontSize="11" fontWeight="900" fill="#0f172a">BID</text>
                </g>
              ))}
              {/* Bid value bubble */}
              <g transform="translate(360, 90)">
                <path d="M0 0 h80 v40 h-40 l-10 12 -4 -12 h-26 z" fill="#fde047" stroke="#0f172a" strokeWidth="1.5" />
                <text x="40" y="26" textAnchor="middle" fontSize="20" fontWeight="900" fill="#0f172a">200</text>
              </g>
            </svg>
          </div>
        </div>
      </section>

      {/* Auctions section */}
      <section id="auctions" className="max-w-6xl mx-auto -mt-8 sm:-mt-14 relative z-10 px-4 pb-14">
        <div className="bg-white shadow-2xl rounded-xl px-6 sm:px-10 py-8">
          <div className="flex flex-wrap items-center justify-center gap-2 mb-5" data-testid="auction-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`text-sm font-bold uppercase tracking-wide px-4 py-2 rounded transition-colors ${tab === t.key ? "bg-blue-600 text-white shadow" : "text-gray-600 hover:text-blue-600"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            {TABS.find((t) => t.key === tab)?.label}
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px bg-red-500 flex-1 max-w-xs" />
            <Trophy size={16} className="text-red-500" weight="fill" />
            <div className="h-px bg-red-500 flex-1 max-w-xs" />
          </div>
          <AuctionsCarousel tabKey={tab} />
        </div>
      </section>

      {/* About section */}
      <section id="about" className="max-w-6xl mx-auto px-4 py-14">
        <h2 className="text-center text-3xl sm:text-4xl font-bold text-gray-900">About NitiSoft Auction</h2>
        <div className="flex items-center justify-center gap-3 mb-8 mt-2">
          <div className="h-px bg-red-500 flex-1 max-w-xs" />
          <Trophy size={16} className="text-red-500" weight="fill" />
          <div className="h-px bg-red-500 flex-1 max-w-xs" />
        </div>
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="flex justify-center">
            <svg viewBox="0 0 400 300" className="w-full max-w-sm" aria-hidden="true">
              <ellipse cx="200" cy="270" rx="160" ry="16" fill="rgba(0,0,0,0.08)" />
              <rect x="60" y="120" width="120" height="140" rx="10" fill="#ea580c" />
              <rect x="70" y="130" width="100" height="120" rx="6" fill="#f97316" />
              <circle cx="120" cy="180" r="30" fill="white" />
              <text x="120" y="187" textAnchor="middle" fontWeight="900" fontSize="18" fill="#ea580c">BID</text>
              {/* Gavel */}
              <line x1="200" y1="140" x2="270" y2="90" stroke="#78350f" strokeWidth="8" strokeLinecap="round" />
              <rect x="250" y="55" width="70" height="40" rx="8" fill="#f97316" />
              <rect x="220" y="230" width="80" height="20" rx="4" fill="#78350f" />
              {/* Trophy */}
              <path d="M280 200 Q320 200 320 240 L320 260 L280 260 Z" fill="#eab308" />
              <path d="M320 200 Q360 200 360 240 L360 260 L320 260 Z" fill="#eab308" />
            </svg>
          </div>
          <div className="text-gray-700 leading-relaxed">
            <p className="mb-3">
              Every tournament organizer knows how tough it is to sort entry forms, plan draws and run matches without hiccups. <strong>NitiSoft Auction</strong> takes all of that off your plate — a purpose-built platform that turns a chaotic auction day into a clean, broadcast-ready event.
            </p>
            <p className="mb-3">
              Live bidding, LED &amp; stream overlays, per-team purses with max-bid logic, one-tap owner links, Excel-based bulk import, reports and audit trails — everything&apos;s ready out of the box for cricket, hockey, volleyball, basketball, and any format you run.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-6">
              {["Live Bidding", "Broadcast Overlays", "Team Management", "Reports & Audit"].map((f) => (
                <div key={f} className="border border-gray-200 rounded px-3 py-2 bg-gray-50 text-sm font-semibold text-gray-800">
                  ✓ {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="bg-gray-900 text-gray-300 py-8 text-center">
        <div className="max-w-7xl mx-auto px-4 space-y-2">
          <img src="/nitisoft-logo.png" alt="" className="h-10 mx-auto opacity-90" />
          <div className="text-sm">© {new Date().getFullYear()} NitiSoft Technologies · <a href={`tel:${CONTACT_PHONE_TEL}`} className="hover:text-white">{CONTACT_PHONE}</a></div>
          <div className="text-xs text-gray-500">Made for tournament organizers — everywhere.</div>
        </div>
      </footer>
    </div>
  );
}
